import type { PhenomenonPayload } from "atoms/Ltool/newConfigAtoms";
import type { Powertrain } from "atoms/powertrainAtom";
import type { AchievementConfigData } from "api/fetchLtool";
import type { AchievementLevelRequestBody } from "api/submitLtool";
import { CONDITION_GROUP_IDS } from "utils/constsHev";

// 達成度設定の補正値（three_sigma）・マージン（risk_margin）を並べる現象グループの順序。
// BEV は同じ画面の freq ブロック（PHENOMENON_IDS 順）や達成度判断表の列順に揃えて aco → vib → gn。
// PHENOMENON_GROUP_IDS（vib → gn → aco）は Matrix のグループ選択の既定値に使われているため流用しない
// （構成要素が食い違わないことはテストで固定する）。
// HEV は全画面で gn → vib のため CONDITION_GROUP_IDS をそのまま使う。
export const ACHIEVEMENT_GROUP_IDS: Record<Powertrain, readonly string[]> = {
    bev: ["aco", "vib", "gn"],
    hev: CONDITION_GROUP_IDS,
};

export const createEmptyPayload = (): PhenomenonPayload => ({
    three_sigma: {},
    risk_margin: {},
    freq: {},
});

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

// 入力欄の文字列と GET 返値の両方を数値に正規化する。数値文字列は許容し（BE が Decimal を
// 文字列で返しても欄が空にならないように）、空文字・真偽値・NaN・Infinity は null（入力漏れ扱い）。
export const toNumberOrNull = (value: unknown): number | null => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const pickGroupValues = (source: unknown, groupIds: readonly string[]) => {
    const record = isRecord(source) ? source : {};
    return Object.fromEntries(groupIds.map((id) => [id, toNumberOrNull(record[id])]));
};

// GET achievement_config の返値を、全欄に初期表示するフォーム状態へ変換する。
// 返値の形が想定外（キー欠落・旧形式のスカラー three_sigma など）でも落とさず null にする。
// 欠落は hasIncompleteGroupValues で検知でき、送信前検証（buildAchievementLevelRequest）でも弾かれる。
export const toFormState = (
    data: Partial<AchievementConfigData> | null | undefined,
    groupIds: readonly string[],
    phenomenonIds: readonly string[],
): PhenomenonPayload => {
    const rawFreq: unknown = data?.freq;
    const freq = isRecord(rawFreq) ? rawFreq : {};
    return {
        three_sigma: pickGroupValues(data?.three_sigma, groupIds),
        risk_margin: pickGroupValues(data?.risk_margin, groupIds),
        freq: Object.fromEntries(
            phenomenonIds.map((id) => {
                const raw = freq[id];
                const entry = isRecord(raw) ? raw : {};
                return [id, { min: toNumberOrNull(entry.min), max: toNumberOrNull(entry.max) }];
            }),
        ),
    };
};

// BE は常に全グループ分の値を返す契約。補正値・マージンに欠落があれば契約不整合（旧形式の
// 応答など）の可能性があるため、呼び出し側が通知できるよう検知する。freq は対象外。
export const hasIncompleteGroupValues = (
    form: PhenomenonPayload,
    groupIds: readonly string[],
): boolean =>
    groupIds.some(
        (id) =>
            toNumberOrNull(form.three_sigma[id]) === null ||
            toNumberOrNull(form.risk_margin[id]) === null,
    );

// マージンは 0 以上のみ有効（BE が負値を拒否する）。入力中の欄エラー表示に使う。
export const isNegativeRiskMargin = (value: number | null | undefined): boolean => {
    const parsed = toNumberOrNull(value);
    return parsed !== null && parsed < 0;
};

export type BuildAchievementLevelRequestResult =
    | { ok: true; body: AchievementLevelRequestBody }
    | { ok: false; message: string };

type CollectResult = { values: Record<string, number> } | { message: string };

// 全グループの値を数値で集める。1 つでも未入力ならその項目名で、validateEach が文言を返せばそれで失敗にする。
const collectGroupValues = (
    source: Record<string, number | null>,
    groupIds: readonly string[],
    fieldName: string,
    validateEach?: (value: number) => string | null,
): CollectResult => {
    const values: Record<string, number> = {};
    for (const id of groupIds) {
        const value = toNumberOrNull(source[id]);
        if (value === null) return { message: `${fieldName}に未入力の項目があります。` };
        const violation = validateEach?.(value);
        if (violation) return { message: violation };
        values[id] = value;
    }
    return { values };
};

// フォーム状態を POST body に変換する。BE の受け付け条件（対象グループすべてのキー・数値のみ・
// マージンは 0 以上・余分なキー不可）を送信前に満たし、422 を起こさないようにする。
// 補正値（three_sigma）は負値も可。
export const buildAchievementLevelRequest = (
    form: PhenomenonPayload,
    groupIds: readonly string[],
    phenomenonIds: readonly string[],
): BuildAchievementLevelRequestResult => {
    const threeSigma = collectGroupValues(form.three_sigma, groupIds, "補正値（three_sigma）");
    if ("message" in threeSigma) return { ok: false, message: threeSigma.message };

    const riskMargin = collectGroupValues(
        form.risk_margin,
        groupIds,
        "マージン（risk_margin）",
        (value) => (value < 0 ? "マージン（risk_margin）は 0 以上で入力してください。" : null),
    );
    if ("message" in riskMargin) return { ok: false, message: riskMargin.message };

    const freq: Record<string, { min: number; max: number }> = {};
    for (const id of phenomenonIds) {
        const min = toNumberOrNull(form.freq[id]?.min);
        const max = toNumberOrNull(form.freq[id]?.max);
        if (min === null || max === null) {
            return { ok: false, message: "周波数範囲に未入力の項目があります。" };
        }
        freq[id] = { min, max };
    }

    return {
        ok: true,
        body: { three_sigma: threeSigma.values, risk_margin: riskMargin.values, freq },
    };
};
